import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { localizeDigits } from '@/lib/localize-digits'

export interface RadiusControlProps {
  searchRadius: number
  showManualInput: boolean
  manualRadiusValue: string
  onRadiusChange: (radius: number) => void
  onManualValueChange: (value: string) => void
  onShowManualInput: (show: boolean) => void
}

export function RadiusControl({
  searchRadius,
  showManualInput,
  manualRadiusValue,
  onRadiusChange,
  onManualValueChange,
  onShowManualInput,
}: RadiusControlProps) {
  const { t } = useTranslation()

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium">{t('search.radius')}</label>
        <div className="flex items-center gap-2">
          {showManualInput ? (
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="100"
                max="2000"
                step="10"
                value={manualRadiusValue}
                onChange={(e) => {
                  const value = e.target.value
                  onManualValueChange(value)
                  const numValue = Number(value)
                  if (!isNaN(numValue) && numValue >= 100 && numValue <= 2000) {
                    onRadiusChange(numValue)
                  }
                }}
                onBlur={() => {
                  const numValue = Number(manualRadiusValue)
                  if (isNaN(numValue) || numValue < 100) {
                    onManualValueChange('100')
                    onRadiusChange(100)
                  } else if (numValue > 2000) {
                    onManualValueChange('2000')
                    onRadiusChange(2000)
                  } else {
                    onRadiusChange(numValue)
                    onManualValueChange(numValue.toString())
                  }
                }}
                className="w-16 px-2 py-1 text-sm border border-border rounded bg-background text-right"
                aria-label={t('search.radius')}
                autoFocus
              />
              <span className="text-xs text-muted-foreground">m</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                aria-label="Close manual input"
                onClick={() => {
                  onShowManualInput(false)
                  onManualValueChange(searchRadius.toString())
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {searchRadius < 1000
                  ? localizeDigits(`${searchRadius}m`)
                  : localizeDigits(`${(searchRadius / 1000).toFixed(1)} km`)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                aria-label="Enter radius manually"
                onClick={() => {
                  onManualValueChange(searchRadius.toString())
                  onShowManualInput(true)
                }}
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
      {!showManualInput && (
        <>
          <input
            type="range"
            min="100"
            max="2000"
            step="10"
            value={searchRadius}
            onChange={(e) => {
              const value = Number(e.target.value)
              onRadiusChange(value)
              onManualValueChange(value.toString())
            }}
            className="w-full"
            aria-label={t('search.radius')}
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{t('search.rangeMin')}</span>
            <span>{t('search.rangeMax')}</span>
          </div>
        </>
      )}
    </div>
  )
}
